import { forwardRef, useCallback, useImperativeHandle, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  selectEmployeeInformation,
  updateJobField,
} from "../../../Redux/employeeInformationSlice.js";
import styles from "./JobInformation.module.scss";

const selectOptions = {
  job_title: ["Select", "CODOID ZONE"],
  client_id: ["Select", "Denave test"],
  designation_id: ["Select", "test"],
  rm_id: ["Select", "denave rm denave rm"],
  pm_id: ["Select", "denave payroll denave payroll"],
  vh_id: ["Select", "denaveonboarder denaveonboarder"],
  project_nature: ["Select", "Managed", "Staffing", "Consulting"],
  billabilty: ["Select", "Billable", "Non Billable"],
  billing_type: ["Select", "Fixed", "Variable"],
};

const fields = [
  { name: "job_title", label: "Site", type: "select", required: true },
  { name: "client_id", label: "Client", type: "select", required: true },
  {
    name: "designation_id",
    label: "Designation",
    type: "select",
    required: true,
  },
  {
    name: "gross_salary",
    label: "Indicative Salary",
    required: true,
  },
  { name: "rm_id", label: "RM", type: "select", required: true },
  { name: "pm_id", label: "PM", type: "select", required: true },
  { name: "vh_id", label: "VH", type: "select", required: true },

  { name: "project_code", label: "Project Code", required: true },
  { name: "program_code", label: "Program Code", required: true },
  { name: "client_code", label: "Client Code", required: true },
  { name: "division_code", label: "Division Code", required: true },
  { name: "division_name", label: "Division Name", required: true },

  {
    name: "medical_insurance",
    label: "Medical Insurance",
    required: true,
  },
  {
    name: "accident_insurance",
    label: "Accident Insurance",
    required: true,
  },
  {
    name: "premium_insurance",
    label: "Premium Insurance",
    required: true,
  },

  { name: "variable_salary", label: "Variable Salary", required: true },
  {
    name: "stationary_bonus",
    label: "Statutory Bonus",
    required: true,
  },
  {
    name: "professional_tax",
    label: "Professional Tax",
    required: true,
  },
  { name: "bonus", label: "Bonus", required: true },
  {
    name: "mobile_expenses",
    label: "Mobile Expenses",
    required: true,
  },
  {
    name: "conveyance",
    label: "Conveyance Reimbursement",
    required: true,
  },
  {
    name: "retention_bonus",
    label: "Retention Bonus",
    required: true,
  },

  {
    name: "is_gratuity",
    label: "Gratuity",
    type: "checkbox",
  },

  { name: "da_salary", label: "DA Salary" },
  { name: "ca_salary", label: "CA Salary", required: true },
  { name: "swa_salary", label: "SWA Salary" },

  {
    name: "project_nature",
    label: "Project Nature",
    type: "select",
    required: true,
  },
  {
    name: "billabilty",
    label: "Billing Ability",
    type: "select",
    required: true,
  },
  {
    name: "billing_type",
    label: "Billing Type",
    type: "select",
    required: true,
  },

  {
    name: "addBasicSalary",
    label: "Add Basic Salary",
    type: "checkbox",
  },

  { name: "basic_salary", label: "Basic Salary", required: true },
  { name: "hra_salary", label: "HRA", required: true },
  {
    name: "other_allowances_salary",
    label: "Other Allowances",
  },
  { name: "employee_ESI", label: "Employee ESI" },
  { name: "employee_PF", label: "Employee PF" },
  {
    name: "airtime_allowance",
    label: "Airtime Allowance",
  },
  { name: "net_salary", label: "Net Salary" },

  {
    name: "job_status",
    label: "Job Status",
  },
  {
    name: "end_date",
    label: "End Date",
    type: "date",
  },
];

const numericFields = new Set([
  "gross_salary",
  "project_code",
  "program_code",
  "client_code",
  "division_code",
  "medical_insurance",
  "accident_insurance",
  "premium_insurance",
  "variable_salary",
  "stationary_bonus",
  "professional_tax",
  "bonus",
  "mobile_expenses",
  "conveyance",
  "retention_bonus",
  "da_salary",
  "ca_salary",
  "swa_salary",
  "basic_salary",
  "hra_salary",
  "other_allowances_salary",
  "employee_ESI",
  "employee_PF",
  "airtime_allowance",
  "net_salary",
]);

const JobInformation = forwardRef(function JobInformation(_, ref) {
  const dispatch = useDispatch();
  const form = useSelector(
    (state) => selectEmployeeInformation(state).jobInformation,
  );

  const [errorFields, setErrorFields] = useState(new Set());

  const clearError = (name) => {
    setErrorFields((prev) => {
      const next = new Set(prev);
      next.delete(name);
      return next;
    });
  };

  const updateField = (name, value) => {
    dispatch(
      updateJobField({
        name,
        value:
          numericFields.has(name) && typeof value === "string"
            ? value.replace(/[^\d,]/g, "")
            : value,
      }),
    );
    clearError(name);
  };

  const getMissingFields = useCallback(() => {
    const missingObjects = fields.filter((field) => {
      if (!field.required) return false;

      // Salary breakup is required only when Add Basic Salary is enabled
      const salaryBreakupFields = [
        "basic_salary",
        "hra_salary",
        "other_allowances_salary",
        "employee_ESI",
        "employee_PF",
        "airtime_allowance",
        "net_salary",
      ];

      if (salaryBreakupFields.includes(field.name) && !form.addBasicSalary) {
        return false;
      }

      const value = form[field.name];

      if (field.type === "select") {
        return !value || value === "Select";
      }

      if (field.type === "checkbox") {
        return false;
      }

      return !String(value ?? "").trim();
    });

    setErrorFields(new Set(missingObjects.map((f) => f.name)));

    return missingObjects.map((f) => f.label);
  }, [form]);

  useImperativeHandle(
    ref,
    () => ({
      getMissingFields,
      isComplete: () => getMissingFields().length === 0,
    }),
    [getMissingFields],
  );

  const renderField = (field) => {
    // Hide salary breakup fields unless enabled
    const salaryBreakupFields = [
      "basic_salary",
      "hra_salary",
      "other_allowances_salary",
      "employee_ESI",
      "employee_PF",
      "airtime_allowance",
      "net_salary",
    ];

    if (salaryBreakupFields.includes(field.name) && !form.addBasicSalary) {
      return null;
    }

    if (field.type === "checkbox") {
      return (
        <label className={styles.checkboxField} key={field.name}>
          <span>
            {field.label}
            {field.required ? <b>*</b> : null}
          </span>
          <input
            type="checkbox"
            checked={Boolean(form[field.name])}
            onChange={(event) => {
              dispatch(
                updateJobField({
                  name: field.name,
                  value: event.target.checked,
                }),
              );
              clearError(field.name);
            }}
          />
        </label>
      );
    }

    if (field.type === "select") {
      return (
        <label className={styles.field} key={field.name}>
          <span>
            {field.label}
            {field.required ? <b>*</b> : null}
          </span>
          <select
            value={form[field.name] || "Select"}
            className={errorFields.has(field.name) ? styles.inputError : ""}
            onChange={(event) => updateField(field.name, event.target.value)}
          >
            {selectOptions[field.name].map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
          {errorFields.has(field.name) && (
            <p className={styles.fieldError}>Please select an option</p>
          )}
        </label>
      );
    }

    return (
      <label className={styles.field} key={field.name}>
        <span>
          {field.label}
          {field.required ? <b>*</b> : null}
        </span>
        <input
          type={field.type || "text"}
          value={form[field.name] || ""}
          inputMode={numericFields.has(field.name) ? "numeric" : undefined}
          className={errorFields.has(field.name) ? styles.inputError : ""}
          onChange={(event) => updateField(field.name, event.target.value)}
        />
        {errorFields.has(field.name) && (
          <p className={styles.fieldError}>Please fill this field</p>
        )}
      </label>
    );
  };

  return (
    <div className={styles.jobInformation}>
      <header className={styles.sectionHeader}>Job Information</header>
      <div className={styles.content}>
        <div className={styles.formGrid}>{fields.map(renderField)}</div>
      </div>
    </div>
  );
});

export default JobInformation;
